/* eslint-disable */
import { createApi } from '@reduxjs/toolkit/query/react'
import { USERSERVICE_BASE_URL } from '../../envVariables'
import { axiosBaseQuery } from '../../api/BaseQuery'
import api from '../../api'

import type {
  PbiReportDefinitionViewModel,
  PbiReportInstanceViewModel,
  AdminPbiReportSearchFilter,
} from '@rsmus/ecp-userservice'

export type ReportsData = {
  data: PbiReportDefinitionViewModel[]
  totalRows: number
}
export type PbiReportInstanceRow = PbiReportInstanceViewModel & {
  reportDefinitionName?: string | null
}
export type ReportInstancesData = {
  data: PbiReportInstanceRow[]
  totalRows: number
}
export type PbiChildReportRow = {
  id?: number
  pbiReportDefinitionId?: number
  parentReportDefinitionId?: number
  displayName?: string | null
  childReportName?: string | null
  pbiWorkspaceId?: string | null
  pbiObjectId?: string | null
  isDashboard?: boolean
  parentReportName?: string | null
}

export type ChildReportsData = {
  data: PbiChildReportRow[]
  totalRows: number
}

let pbireportSearchApiController = new AbortController()
let pbireportInstanceSearchApiController = new AbortController()
let pbiChildReportSearchApiController = new AbortController()

const pbiReportSearchService = createApi({
  reducerPath: 'pbiReportSearchService',
  tagTypes: [
    'searchFilters',
    'instanceSearchFilters',
    'childReportSearchFilters',
  ],
  baseQuery: axiosBaseQuery({
    baseURL: USERSERVICE_BASE_URL,
  }),
  endpoints: (builder) => ({
    updateReportSearchResults: builder.mutation<
      ReportsData,
      AdminPbiReportSearchFilter
    >({
      async queryFn(filter) {
        if (!pbireportSearchApiController.signal.aborted) {
          pbireportSearchApiController.abort()
        }
        pbireportSearchApiController = new AbortController()

        try {
          const res = await api.user.report_GetAllReports(filter, undefined)
          const response: any = (res as any)?.data ?? res
          const rows: PbiReportDefinitionViewModel[] = Array.isArray(
            response?.result,
          )
            ? response.result
            : Array.isArray(response?.data)
              ? response.data
              : Array.isArray(response)
                ? response
                : []

          const totalRows: number =
            typeof response?.totalRows === 'number'
              ? response.totalRows
              : typeof response?.data?.totalRows === 'number'
                ? response.data.totalRows
                : rows.length

          return { data: { data: rows, totalRows } }
        } catch (error) {
          return { error: error as any }
        }
      },
    }),

    updateReportInstanceSearchResults: builder.mutation<
      ReportInstancesData,
      AdminPbiReportSearchFilter
    >({
      async queryFn(filter) {
        if (!pbireportInstanceSearchApiController.signal.aborted) {
          pbireportInstanceSearchApiController.abort()
        }
        pbireportInstanceSearchApiController = new AbortController()

        try {
          const res = await api.user.report_GetAllReportInstances(
            null,
            filter,
            undefined,
          )

          const response: any = (res as any)?.data ?? res

          const rows: PbiReportInstanceRow[] = Array.isArray(response?.result)
            ? response.result
            : Array.isArray(response?.data)
              ? response.data
              : Array.isArray(response)
                ? response
                : []

          const totalRows: number =
            typeof response?.totalRows === 'number'
              ? response.totalRows
              : typeof response?.data?.totalRows === 'number'
                ? response.data.totalRows
                : rows.length

          return { data: { data: rows, totalRows } }
        } catch (error) {
          return { error: error as any }
        }
      },
    }),

    updateChildReportSearchResults: builder.mutation<
      ChildReportsData,
      AdminPbiReportSearchFilter
    >({
      async queryFn(filter) {
        if (!pbiChildReportSearchApiController.signal.aborted) {
          pbiChildReportSearchApiController.abort()
        }
        pbiChildReportSearchApiController = new AbortController()

        try {
          const res = await (api.user as any).report_GetAllChildReports(
            filter,
            undefined,
          )

          const response: any = (res as any)?.data ?? res

          const rows: PbiChildReportRow[] = Array.isArray(response?.result)
            ? response.result
            : Array.isArray(response?.data)
              ? response.data
              : Array.isArray(response)
                ? response
                : []

          const totalRows: number =
            typeof response?.totalRows === 'number'
              ? response.totalRows
              : typeof response?.data?.totalRows === 'number'
                ? response.data.totalRows
                : rows.length

          return { data: { data: rows, totalRows } }
        } catch (error) {
          return { error: error as any }
        }
      },
    }),
  }),
})

// Export hooks and API
export const {
  useUpdateReportSearchResultsMutation,
  useUpdateReportInstanceSearchResultsMutation,
  useUpdateChildReportSearchResultsMutation,
} = pbiReportSearchService

/**
 * Function to reset the report search API controller.
 * Call this on component unmount to clean up pending requests.
 */
export const resetReportSearch = () => {
  if (!pbireportSearchApiController.signal.aborted) {
    pbireportSearchApiController.abort('Resetting search controller.')
  }
  pbireportSearchApiController = new AbortController()
}

/**
 * Function to reset the instance search API controller.
 * Call this on component unmount to clean up pending requests.
 */
export const resetInstanceSearch = () => {
  if (!pbireportInstanceSearchApiController.signal.aborted) {
    pbireportInstanceSearchApiController.abort(
      'Resetting instance search controller.',
    )
  }
  pbireportInstanceSearchApiController = new AbortController()
}

/**
 * Function to reset the child report search API controller.
 * Call this on component unmount to clean up pending requests.
 */
export const resetChildReportSearch = () => {
  if (!pbiChildReportSearchApiController.signal.aborted) {
    pbiChildReportSearchApiController.abort(
      'Resetting child report search controller.',
    )
  }
  pbiChildReportSearchApiController = new AbortController()
}

export default pbiReportSearchService
